import React, { useMemo, useCallback, useState } from 'react';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { escapeHtml } from '../../utils/escapeHtml';
import {
    faEdit,
    faEye,
    faCheck,
    faTimes,
    faShoppingCart,
    faSpinner,
    faTruck,
    faPrint,
    faTruckLoading,
    faCheckDouble,
    faHistory,
} from '@fortawesome/free-solid-svg-icons';
import { UI_COLORS, colorHelpers } from '../../utils/colors';
import AuditLogsModal from '../common/AuditLogsModal';
import DataTable from '../common/DataTable';
import i18n from '../../i18n';
import { useTranslation } from 'react-i18next';
import purchaseOrderService from '../../services/purchaseOrderService';
import { useRole } from '../../context/roleContext';
import { USER_ROLES, AUDIT_LOG_VIEWER_ROLES } from '../../utils/constants';

const ITEMS_PER_PAGE = 25;

const formatDate = (dateString) => {
    if (!dateString) return i18n.t('common.symbols.emDash');
    return new Date(dateString).toLocaleDateString('en-GB', {
        year: 'numeric',
        month: 'short',
        day: 'numeric',
    });
};

const getStatusBadge = (status) => {
    const s = (status || '').toString().toLowerCase();
    // Use Purchase Order statuses as requested
    const getLabel = (key, fallback) => {
        if (!key) return fallback || String(status);
        if (key === 'supervisor_assigned') return 'Sup. Assgn.';
        return (i18n.exists(`purchaseOrders.statuses.${key}`) && i18n.t(`purchaseOrders.statuses.${key}`)) ||
            (i18n.exists(`purchaseAdvice.statuses.${key}`) && i18n.t(`purchaseAdvice.statuses.${key}`)) ||
            fallback || String(status);
    };

    if (s === 'approved' || s === 'completed' || s === 'delivered' || s === 'invoiced' || s === 'received') {
        return (
            <div className="flex items-center space-x-2">
                <FontAwesomeIcon icon={faCheck} className="text-sm" style={{ color: UI_COLORS.STATUS.SUCCESS }} />
                <span className="text-sm font-medium" style={{ color: UI_COLORS.STATUS.SUCCESS }}>{getLabel(s, String(status))}</span>
            </div>
        );
    }
    if (s === 'rejected' || s === 'cancelled' || s === 'failed') {
        return (
            <div className="flex items-center space-x-2">
                <FontAwesomeIcon icon={faTimes} className="text-sm" style={{ color: UI_COLORS.STATUS.ERROR }} />
                <span className="text-sm font-medium" style={{ color: UI_COLORS.STATUS.ERROR }}>{getLabel(s, String(status))}</span>
            </div>
        );
    }
    if (s === 'ready_for_challan') {
        return (
            <div className="flex items-center space-x-2">
                <FontAwesomeIcon icon={faCheckDouble} className="text-sm" style={{ color: '#0d9488' }} />
                <span className="text-sm font-medium" style={{ color: '#0d9488' }}>Ready for Challan</span>
            </div>
        );
    }
    if (s === 'delivery_assigned' || s === 'in_transit') {
        return (
            <div className="flex items-center space-x-2">
                <FontAwesomeIcon icon={faTruck} className="text-sm" style={{ color: UI_COLORS.STATUS.INFO }} />
                <span className="text-sm font-medium" style={{ color: UI_COLORS.STATUS.INFO }}>{getLabel(s, String(status))}</span>
            </div>
        );
    }
    if (['loading', 'loading_started', 'truck_loading', 'truck_load_started', 'truck_loading_started', 'loading_in_progress', 'truck_load'].includes(s)) {
        return (
            <div className="flex items-center space-x-2">
                <FontAwesomeIcon icon={faSpinner} spin className="text-sm" style={{ color: UI_COLORS.STATUS.INFO }} />
                <span className="text-sm font-medium" style={{ color: UI_COLORS.STATUS.INFO }}>{getLabel(s, String(status))}</span>
            </div>
        );
    }
    if (s === 'truck_loaded' || s === 'loaded' || s === 'truck_load_completed') {
        return (
            <div className="flex items-center space-x-2">
                <FontAwesomeIcon icon={faCheck} className="text-sm" style={{ color: UI_COLORS.STATUS.SUCCESS }} />
                <span className="text-sm font-medium" style={{ color: UI_COLORS.STATUS.SUCCESS }}>{getLabel(s, 'Truck Loaded')}</span>
            </div>
        );
    }
    if (s === 'unloaded' || s === 'truck_unloaded') {
        return (
            <div className="flex items-center space-x-2">
                <FontAwesomeIcon icon={faCheckDouble} className="text-sm" style={{ color: UI_COLORS.STATUS.SUCCESS }} />
                <span className="text-sm font-medium" style={{ color: UI_COLORS.STATUS.SUCCESS }}>{getLabel(s, 'Unloaded')}</span>
            </div>
        );
    }

    return (
        <div className="flex items-center space-x-2">
            <FontAwesomeIcon icon={faShoppingCart} className="text-sm" style={{ color: UI_COLORS.STATUS.WARNING }} />
            <span className="text-sm font-medium" style={{ color: UI_COLORS.STATUS.WARNING }}>{getLabel(s, String(status))}</span>
        </div>
    );
};

const PurchaseAdviceTable = ({
    advices = [],
    loading = false,
    onViewAdvice,
    onEditAdvice,
    onAssignSupervisor,
    onStartLoading,
    onCompleteLoading,
    onCompleteUnloading,
    onStartUnloading,
    onGenerateChallan,
    onMarkComplete,
    onEditUnloading,
    viewLoading = false,
}) => {
    const { t } = useTranslation();
    const { userRole } = useRole();
    const [printingAdviceId, setPrintingAdviceId] = useState(null);
    const [auditLogTarget, setAuditLogTarget] = useState(null);

    const isSalesStaff = userRole === USER_ROLES.SALES_STAFF || userRole === USER_ROLES.OPERATIONS_MANAGER;
    const isSupervisor = userRole === USER_ROLES.DELIVERY_SUPERVISOR;

    const handlePrintAdvice = useCallback(async (adviceOrId) => {
        const id = typeof adviceOrId === 'object' ? adviceOrId.id : adviceOrId;
        if (!id) return;
        setPrintingAdviceId(id);
        let advice = null;
        try {
            const res = await purchaseOrderService.getPurchaseAdviceById(id);
            if (res && res.success) advice = res.data;
        } catch (e) { }

        if (!advice) {
            if (typeof adviceOrId === 'object') advice = adviceOrId;
        }

        if (!advice) {
            setPrintingAdviceId(null);
            alert(t('common.messages.unableToLoadAdviceForPrinting'));
            return;
        }

        const safe = (v) => (v === null || typeof v === 'undefined' || v === '' ? '' : escapeHtml(v)); // print window (P1-2)
        const fmt = (d) => (d ? new Date(d).toLocaleDateString('en-GB', { day: 'numeric', month: 'long', year: 'numeric' }) : '');
        const fmtTime = (d) => (d ? new Date(d).toLocaleTimeString('en-US', { hour: '2-digit', minute: '2-digit', hour12: true }) : '');
        const nowTime = new Date().toLocaleTimeString('en-US', { hour: '2-digit', minute: '2-digit', hour12: true });

        const items = Array.isArray(advice.items) ? advice.items.filter(it => it && Object.keys(it).length > 0) : [];
        
        const itemsRows = items.map((it, idx) => {
            const actualQty = safe(it.actual_quantity) !== '' && safe(it.actual_quantity) !== '0.00' ? Math.ceil(parseFloat(it.actual_quantity)) : '-';
            const standardLength = safe(it.standard_length) ? `${safe(it.standard_length)} m` : '-';
            const standardWeight = safe(it.standard_weight) ? `${safe(it.standard_weight)} kg` : '-';
            
            return `
      <tr>
        <td>${idx + 1}</td>
        <td>${safe(it.size_name || it.product_name)}<br/><span style="font-size: 7px; color: #666;">(${safe(it.product_name)})</span></td>
        <td class="text-right">${safe(Math.ceil(parseFloat(it.quantity || 0)))} ${safe(it.unit)}</td>
        <td class="text-right">${actualQty !== '-' ? `${actualQty} ${safe(it.unit)}` : '-'}</td>
        <td class="text-right">${safe(Math.ceil(parseFloat(it.po_item_quantity || 0)))} ${safe(it.unit)}</td>
        <td class="text-right">${standardLength}</td>
        <td class="text-right">${standardWeight}</td>
        <td>${safe(it.remark) || '-'}</td>
      </tr>`;
        }).join('\n');

        const html = `<!doctype html>
<html lang="en">
<head>
  <meta charset="utf-8" />
  <title>Purchase Advice - ${safe(advice.advice_number)}</title>
  <style>
    @page { 
      size: A4;
      margin: 8mm;
    }
    * { box-sizing: border-box; font-family: "Segoe UI", Tahoma, Geneva, Verdana, sans-serif; color: #333; }
    body { margin: 0; padding: 0; background: #fff; line-height: 1.2; font-size: 9px; }
    .no-print { padding: 10px; background: #f0f0f0; border-bottom: 1px solid #ddd; }
    .print-container { max-width: 210mm; margin: 0 auto; padding: 8mm; }
    .title { text-align: center; font-size: 14px; font-weight: 700; margin-bottom: 10px; text-transform: uppercase; letter-spacing: 0.5px; }
    
    .header-grid { display: grid; grid-template-columns: 1fr 1fr; gap: 3px 12px; margin-bottom: 10px; font-size: 9px; border: 1px solid #ddd; padding: 8px; background: #fafafa; }
    .header-field { display: flex; justify-content: flex-start; gap: 4px; margin-bottom: 2px; }
    .header-label { font-weight: 700; min-width: 80px; color: #555; }
    .header-value { color: #000; font-weight: 500; }
    
    .section-title { font-size: 10px; font-weight: 700; margin: 12px 0 6px; text-transform: uppercase; color: #444; border-bottom: 2px solid #333; padding-bottom: 3px; }
    
    table { width: 100%; border-collapse: collapse; margin-bottom: 8px; font-size: 8px; border: 1px solid #ddd; }
    th, td { padding: 3px 4px; text-align: left; border: 1px solid #ddd; }
    th { background: #f5f5f5; font-weight: 700; color: #555; text-transform: uppercase; font-size: 7px; letter-spacing: 0.3px; }
    .text-right { text-align: right; }
    
    .footer-section { display: grid; grid-template-columns: 1.3fr 1fr; gap: 12px; margin-top: 8px; align-items: flex-start; font-size: 8px; }
    .info-grid { display: grid; grid-template-columns: 1fr 1fr; gap: 6px; font-size: 8px; }
    .info-field { display: flex; flex-direction: column; padding: 4px; background: #fafafa; border: 1px solid #e0e0e0; border-radius: 3px; }
    .info-label { font-size: 7px; font-weight: 700; color: #666; text-transform: uppercase; margin-bottom: 2px; }
    .info-value { font-size: 9px; font-weight: 600; color: #000; }
    
    .summary-table { width: 100%; border: 1px solid #ddd; background: #f9f9f9; }
    .summary-table td { padding: 3px 6px; border: none; font-size: 9px; }
    .summary-table .label { color: #555; font-weight: 600; }
    .summary-table .value { text-align: right; font-weight: 700; color: #000; }
    .summary-table .total-row td { border-top: 2px solid #333; font-weight: 800; font-size: 10px; padding-top: 6px; background: #e8e8e8; }
    
    .remarks-box { margin-top: 8px; padding: 6px 8px; background: #fffacd; border: 1px dashed #daa520; border-radius: 4px; font-size: 8px; }
    .remarks-label { font-weight: 700; color: #666; display: block; margin-bottom: 3px; }
    
    @media print { 
      .no-print { display: none !important; } 
      .print-container { padding: 0; }
      body { font-size: 9px; }
    }
  </style>
</head>
<body>
  <div class="no-print"><button onclick="window.print()" style="padding: 10px 20px; cursor:pointer;">Print Purchase Advice</button></div>
  <div class="print-container">
    <div class="title">Purchase Advice</div>
    
    <div class="header-grid">
      <div class="header-field"><span class="header-label">Advice Number:</span> <span class="header-value">${safe(advice.advice_number)}</span></div>
      <div class="header-field"><span class="header-label">PO Number:</span> <span class="header-value">${safe(advice.po_number)}</span></div>
      
      <div class="header-field"><span class="header-label">Advice Date:</span> <span class="header-value">${fmt(advice.advice_date)}</span></div>
      <div class="header-field"><span class="header-label">Status:</span> <span class="header-value">${safe(advice.status).toUpperCase()}</span></div>
      
      <div class="header-field"><span class="header-label">Supplier:</span> <span class="header-value">${safe(advice.supplier_name)}</span></div>
      <div class="header-field"><span class="header-label">Warehouse:</span> <span class="header-value">${safe(advice.warehouse_name)}</span></div>
      
      <div class="header-field"><span class="header-label">Time Created:</span> <span class="header-value">${fmtTime(advice.created_at)}</span></div>
      <div class="header-field"><span class="header-label">Time Print:</span> <span class="header-value">${nowTime}</span></div>
    </div>

    <div class="section-title">Items (Total: ${items.length})</div>
    <table>
      <thead>
        <tr>
          <th style="width: 25px;">Sr.</th>
          <th>Description</th>
          <th class="text-right" style="width: 70px;">Quantity</th>
          <th class="text-right" style="width: 75px;">Actual Qty</th>
          <th class="text-right" style="width: 70px;">PO Item Qty</th>
          <th class="text-right" style="width: 60px;">Std Length</th>
          <th class="text-right" style="width: 60px;">Std Weight</th>
          <th style="width: 80px;">Remark</th>
        </tr>
      </thead>
      <tbody>
        ${itemsRows || '<tr><td colspan="8">No items</td></tr>'}
      </tbody>
    </table>

    <div class="footer-section">
      <div class="left-col">
        <div class="section-title">Additional Information</div>
        <div class="info-grid">
          ${safe(advice.supervisor_name) ? `
          <div class="info-field">
            <span class="info-label">Supervisor</span>
            <span class="info-value">${safe(advice.supervisor_name)}</span>
          </div>` : ''}
          
          ${safe(advice.vehicle_number) ? `
          <div class="info-field">
            <span class="info-label">Vehicle Number</span>
            <span class="info-value">${safe(advice.vehicle_number)}</span>
          </div>` : ''}
          
          ${safe(advice.driver_contact) ? `
          <div class="info-field">
            <span class="info-label">Driver Contact</span>
            <span class="info-value">${safe(advice.driver_contact)}</span>
          </div>` : ''}
          
          ${safe(advice.payment_terms) ? `
          <div class="info-field">
            <span class="info-label">Payment Terms</span>
            <span class="info-value">${safe(advice.payment_terms)}</span>
          </div>` : ''}
          
          ${safe(advice.supplier_invoice_number) ? `
          <div class="info-field">
            <span class="info-label">Supplier Invoice No</span>
            <span class="info-value">${safe(advice.supplier_invoice_number)}</span>
          </div>` : ''}
          
          ${safe(advice.supplier_invoice_date) ? `
          <div class="info-field">
            <span class="info-label">Supplier Invoice Date</span>
            <span class="info-value">${fmt(advice.supplier_invoice_date)}</span>
          </div>` : ''}
          
          ${safe(advice.empty_truck_weight) && safe(advice.empty_truck_weight) !== '0.00' ? `
          <div class="info-field">
            <span class="info-label">Empty Truck Weight</span>
            <span class="info-value">${safe(advice.empty_truck_weight)} KG</span>
          </div>` : ''}
          
          ${safe(advice.loaded_truck_weight) && safe(advice.loaded_truck_weight) !== '0.00' ? `
          <div class="info-field">
            <span class="info-label">Loaded Truck Weight</span>
            <span class="info-value">${safe(advice.loaded_truck_weight)} KG</span>
          </div>` : ''}
          
          ${safe(advice.truck_weight) && safe(advice.truck_weight) !== '0.00' ? `
          <div class="info-field">
            <span class="info-label">Net Truck Weight</span>
            <span class="info-value">${safe(advice.truck_weight)} KG</span>
          </div>` : ''}
        </div>
        
        ${safe(advice.remark) || safe(advice.vehicle_remark) ? `
        <div class="remarks-box">
          <span class="remarks-label">Remarks:</span>
          <div>${safe(advice.remark || advice.vehicle_remark)}</div>
        </div>` : ''}
      </div>

      <div class="right-col">
        <div class="section-title">Summary</div>
        <table class="summary-table">
          <tr>
            <td class="label">Total Items</td>
            <td class="value">${safe(advice.total_items)}</td>
          </tr>
          <tr>
            <td class="label">Total Quantity</td>
            <td class="value">${safe(Math.ceil(parseFloat(advice.total_quantity || 0)))}</td>
          </tr>
          ${safe(advice.total_received_quantity) && safe(advice.total_received_quantity) !== '0.0' ? `
          <tr>
            <td class="label">Received Quantity</td>
            <td class="value">${safe(Math.ceil(parseFloat(advice.total_received_quantity || 0)))}</td>
          </tr>` : ''}
          <tr class="total-row">
            <td class="label">Status</td>
            <td class="value">${safe(advice.status).toUpperCase()}</td>
          </tr>
        </table>
      </div>
    </div>

  </div>
</body>
</html>`;

        const w = window.open('', '_blank');
        if (!w) {
            setPrintingAdviceId(null);
            alert(t('common.messages.unableToOpenPrintWindow'));
            return;
        }
        w.document.open();
        w.document.write(html);
        w.document.close();
        try { w.document.title = ''; } catch (e) { }
        setTimeout(() => { try { w.focus(); w.print(); } catch (e) { } finally { setPrintingAdviceId(null); } }, 300);
    }, [t]);

    const columns = useMemo(() => [
        {
            id: 'advice_number',
            label: t('purchaseAdvice.table.columns.adviceNumber'),
            sortable: true,
            sortAccessor: (a) => (a?.advice_number || '').toLowerCase(),
            renderCell: (a) => <span className="text-sm font-medium" style={{ color: UI_COLORS.TEXT.PRIMARY }}>{a.advice_number ? a.advice_number : t('common.symbols.emDash')}</span>,
            skeleton: <div className="w-32 h-4 bg-gray-300 rounded" />,
        },
        {
            id: 'po_number',
            label: t('purchaseAdvice.table.columns.poNumber'),
            sortable: true,
            sortAccessor: (a) => (a?.po_number || '').toLowerCase(),
            renderCell: (a) => (
                <div className="text-sm" style={{ color: UI_COLORS.TEXT.SECONDARY }}>{a.po_number ? a.po_number : t('common.symbols.emDash')}</div>
            ),
            skeleton: <div className="w-32 h-4 bg-gray-300 rounded" />,
        },
        {
            id: 'supplier_name',
            label: t('purchaseAdvice.table.columns.supplierName'),
            sortable: true,
            sortAccessor: (a) => (a?.supplier_name || '').toLowerCase(),
            renderCell: (a) => (
                <div className="text-sm font-medium" style={{ color: UI_COLORS.TEXT.PRIMARY }}>{a.supplier_name ? a.supplier_name : t('common.symbols.emDash')}</div>
            ),
            skeleton: <div className="w-40 h-4 bg-gray-300 rounded" />,
        },
        {
            id: 'date',
            label: t('purchaseAdvice.table.columns.date'),
            sortable: true,
            sortAccessor: (a) => {
                const sortDate = a.updated_at || a.created_at || a.date || a.advice_date;
                return sortDate ? new Date(sortDate).getTime() : 0;
            },
            renderCell: (a) => <span className="text-sm" style={{ color: UI_COLORS.TEXT.SECONDARY }}>{(a.date || a.advice_date) ? formatDate(a.date || a.advice_date) : t('common.symbols.emDash')}</span>,
            skeleton: <div className="w-20 h-4 bg-gray-300 rounded" />,
        },
        // {
        //     id: 'tally_name',
        //     label: t('purchaseAdvice.table.columns.tallyName'),
        //     sortable: true,
        //     sortAccessor: (a) => (a?.tally_name || '').toLowerCase(),
        //     renderCell: (a) => <span className="text-sm" style={{ color: UI_COLORS.TEXT.SECONDARY }}>{a.tally_name ? a.tally_name : t('common.symbols.emDash')}</span>,
        //     skeleton: <div className="w-32 h-4 bg-gray-300 rounded" />,
        // },
        {
            id: 'vehicle_number',
            label: t('purchaseAdvice.table.columns.vehicleNumber'),
            sortable: true,
            sortAccessor: (a) => (a?.vehicle_number || '').toLowerCase(),
            renderCell: (a) => <span className="text-sm font-medium" style={{ color: UI_COLORS.TEXT.PRIMARY }}>{a.vehicle_number ? a.vehicle_number : t('common.symbols.emDash')}</span>,
            skeleton: <div className="w-24 h-4 bg-gray-300 rounded" />,
        },
        {
            id: 'total_quantity',
            label: t('purchaseAdvice.table.columns.totalQuantity'),
            sortable: true,
            sortAccessor: (a) => (a?.total_quantity ? parseFloat(a.total_quantity) : 0),
            renderCell: (a) => {
                // Group quantities by unit from items
                if (a.items && Array.isArray(a.items) && a.items.length > 0) {
                    const quantitiesByUnit = {};
                    
                    // Sum quantities for each unit
                    a.items.forEach(item => {
                        const qty = parseFloat(item.quantity) || 0;
                        const unit = item.unit || '';
                        
                        if (unit) {
                            if (!quantitiesByUnit[unit]) {
                                quantitiesByUnit[unit] = 0;
                            }
                            quantitiesByUnit[unit] += qty;
                        }
                    });
                    
                    // Format as "25 kg, 20 m, 5 pcs"
                    const parts = Object.entries(quantitiesByUnit)
                        .filter(([unit, qty]) => qty > 0)
                        .map(([unit, qty]) => `${Math.ceil(qty)} ${unit}`)
                        .join(', ');
                    
                    if (parts) {
                        return <span className="text-sm font-medium" style={{ color: UI_COLORS.TEXT.PRIMARY }}>{parts}</span>;
                    }
                }
                
                // Fallback to total_quantity if items not available
                if (a.total_quantity) {
                    const unitText = a.unit ? ` ${a.unit}` : '';
                    const hasReceived = typeof a.total_received_quantity === 'number' || a.total_received_quantity;
                    return (
                        <div className="flex flex-col">
                            <span className="text-sm font-medium" style={{ color: UI_COLORS.TEXT.PRIMARY }}>
                                {Math.ceil(parseFloat(a.total_quantity)).toFixed(0)}{unitText}
                            </span>
                            {hasReceived && (
                                <span className="text-xs italic" style={{ color: UI_COLORS.TEXT.SECONDARY }}>
                                    Rec: {Math.ceil(parseFloat(a.total_received_quantity)).toFixed(0)}
                                </span>
                            )}
                        </div>
                    );
                }
                
                return <span className="text-sm font-medium" style={{ color: UI_COLORS.TEXT.PRIMARY }}>{t('common.symbols.emDash')}</span>;
            },
            skeleton: <div className="w-20 h-4 bg-gray-300 rounded" />,
        },
        {
            id: 'supervisor_name',
            label: t('purchaseAdvice.table.columns.supervisorName'),
            sortable: true,
            sortAccessor: (a) => (a?.supervisor_name || '').toLowerCase(),
            renderCell: (a) => (
                <div className="text-sm" style={{ color: UI_COLORS.TEXT.SECONDARY }}>{a.supervisor_name ? a.supervisor_name : t('common.symbols.emDash')}</div>
            ),
            skeleton: <div className="w-32 h-4 bg-gray-300 rounded" />,
        },
        {
            id: 'po_remark',
            label: t('commonCols.purchaseOrdersRemarks'),
            sortable: true,
            sortAccessor: (a) => (a?.po_remark || '').toLowerCase(),
            renderCell: (a) => {
                if (!a.po_remark) return <span style={{ color: UI_COLORS.TEXT.MUTED }}>{t('common.symbols.emDash')}</span>;
                return (
                    <div 
                        className="text-[11px] font-medium break-words leading-relaxed min-w-[160px] max-w-[180px] px-2 py-1.5 rounded border" 
                        title={a.po_remark} 
                        style={{ 
                            backgroundColor: 'rgba(254, 243, 199, 0.5)', 
                            color: '#92400e', 
                            borderColor: 'rgba(217, 119, 6, 0.2)',
                            whiteSpace: 'pre-wrap',
                            display: '-webkit-box',
                            WebkitLineClamp: 5,
                            WebkitBoxOrient: 'vertical',
                            overflow: 'hidden'
                        }}
                    >
                        {a.po_remark}
                    </div>
                );
            },
            skeleton: <div className="w-32 h-4 bg-gray-300 rounded" />,
        },
        {
            id: 'status',
            label: t('purchaseAdvice.table.columns.status'),
            sortable: true,
            align: 'center',
            sortAccessor: (a) => (a?.status || '').toLowerCase(),
            renderCell: (a) => {
                const isSupervisor = userRole === USER_ROLES.DELIVERY_SUPERVISOR;
                const isSupervisorAssigned = (a.status || '').toLowerCase() === 'supervisor_assigned';
                return (
                    <div className="flex flex-col items-center gap-1">
                        {getStatusBadge(a.status)}
                        {isSupervisorAssigned && !isSupervisor && a.supervisor_name && (
                            <span
                                className="text-xs font-semibold px-2 py-0.5 rounded-full"
                                style={{
                                    backgroundColor: 'rgba(234, 179, 8, 0.15)',
                                    color: '#92400e',
                                    border: '1px solid rgba(234, 179, 8, 0.4)',
                                }}
                            >
                                {a.supervisor_name.trim()}
                            </span>
                        )}
                        {isSupervisorAssigned && isSupervisor && a.assigned_by_name && (
                            <span
                                className="text-xs px-2 py-0.5 rounded-full"
                                style={{
                                    backgroundColor: 'rgba(99, 102, 241, 0.08)',
                                    color: '#4338ca',
                                    border: '1px solid rgba(99, 102, 241, 0.2)',
                                }}
                            >
                                By: {a.assigned_by_name.trim()}
                            </span>
                        )}
                    </div>
                );
            },
            skeleton: <div className="w-24 h-4 bg-gray-300 rounded" />,
        },
        {
            id: 'status_actions',
            label: t('purchaseAdvice.table.columns.statusActions'),
            align: 'center',
            sortable: false,
            renderCell: (a) => {
                const status = (a.status || '').toLowerCase();
                const isDirectFlow = !!a.is_direct_flow;
                // Advice flow: pending → supervisor_assigned → unloading → unloaded → challan_generated → received
                // Direct flow: ready_for_challan → challan_generated → received
                const allowedStatuses = ['advice_created', 'pending', 'ready_for_challan', 'supervisor_assigned', 'unloading', 'unloaded', 'challan_generated', 'received'];

                if (!allowedStatuses.includes(status)) {
                    if (status === 'completed') return <span className="text-xs font-medium italic" style={{ color: UI_COLORS.STATUS.SUCCESS }}>{t('purchaseOrders.statuses.completed')}</span>;
                    return null;
                }

                return (
                    <div className="flex flex-wrap items-center justify-center gap-2">
                        {/* Direct flow: Generate Challan from ready_for_challan */}
                        {status === 'ready_for_challan' && !isSupervisor && (
                            <button
                                onClick={() => onGenerateChallan?.(a)}
                                className="px-3 py-1.5 rounded-lg flex items-center gap-2 transition-all duration-200 hover:scale-105 whitespace-nowrap"
                                style={{ color: '#0d9488', backgroundColor: 'rgba(13, 148, 136, 0.08)' }}
                                title={t('common.actions.generateChallan')}
                            >
                                <FontAwesomeIcon icon={faCheckDouble} className="text-xs" />
                                <span className="text-xs font-medium">Generate Challan</span>
                            </button>
                        )}

                        {/* Warehouse flow: Assign Supervisor from pending */}
                        {status === 'pending' && !isDirectFlow && (
                            <button
                                onClick={() => onAssignSupervisor?.(a)}
                                className="px-3 py-1.5 rounded-lg flex items-center gap-2 transition-all duration-200 hover:scale-105 whitespace-nowrap"
                                style={{ color: UI_COLORS.STATUS.INFO, backgroundColor: colorHelpers.withOpacity(UI_COLORS.STATUS.INFO, 0.08) }}
                                title={t('purchaseAdvice.table.tooltips.assignSupervisor')}
                            >
                                <FontAwesomeIcon icon={faTruckLoading} className="text-xs" />
                                <span className="text-xs font-medium">{t('purchaseAdvice.table.tooltips.assignSupervisor')}</span>
                            </button>
                        )}

                        {['supervisor_assigned', 'unloading', 'unloaded'].includes(status) && !isDirectFlow && (
                            <button
                                onClick={() => onAssignSupervisor?.(a)}
                                className="px-3 py-1.5 rounded-lg flex items-center gap-2 transition-all duration-200 hover:scale-105 whitespace-nowrap"
                                style={{ color: UI_COLORS.STATUS.WARNING, backgroundColor: colorHelpers.withOpacity(UI_COLORS.STATUS.WARNING, 0.08) }}
                                title={t('common.actions.editSupervisor')}
                            >
                                <FontAwesomeIcon icon={faTruckLoading} className="text-xs" />
                                <span className="text-xs font-medium">Edit Supervisor</span>
                            </button>
                        )}

                        {status === 'supervisor_assigned' && (
                            isSalesStaff ? (
                                <span className="text-xs font-medium italic" style={{ color: UI_COLORS.TEXT.MUTED }}>
                                    {t('purchaseOrders.table.messages.supervisorUnloading')}
                                </span>
                            ) : (
                                <button
                                    onClick={() => onStartUnloading?.(a)}
                                    className="px-3 py-1.5 rounded-lg flex items-center gap-2 transition-all duration-200 hover:scale-105 whitespace-nowrap"
                                    style={{ color: UI_COLORS.STATUS.INFO, backgroundColor: colorHelpers.withOpacity(UI_COLORS.STATUS.INFO, 0.08) }}
                                    title={t('purchaseAdvice.table.tooltips.startUnloading')}
                                >
                                    <FontAwesomeIcon icon={faTruckLoading} className="text-xs" />
                                    <span className="text-xs font-medium">{t('purchaseAdvice.table.tooltips.startUnloading')}</span>
                                </button>
                            )
                        )}

                        {status === 'unloading' && (
                            isSalesStaff ? (
                                <span className="text-xs font-medium italic" style={{ color: UI_COLORS.TEXT.MUTED }}>
                                    {t('purchaseOrders.table.messages.supervisorUnloading')}
                                </span>
                            ) : (
                                <button
                                    onClick={() => onCompleteUnloading?.(a)}
                                    className="px-3 py-1.5 rounded-lg flex items-center gap-2 transition-all duration-200 hover:scale-105 whitespace-nowrap"
                                    style={{ color: UI_COLORS.STATUS.SUCCESS, backgroundColor: colorHelpers.withOpacity(UI_COLORS.STATUS.SUCCESS, 0.08) }}
                                    title={t('purchaseAdvice.table.tooltips.completeUnloading')}
                                >
                                    <FontAwesomeIcon icon={faCheckDouble} className="text-xs" />
                                    <span className="text-xs font-medium">{t('purchaseAdvice.table.tooltips.completeUnloading')}</span>
                                </button>
                            )
                        )}

                        {status === 'unloaded' && (
                            <>
                                <button
                                    onClick={() => onEditUnloading?.(a)}
                                    className="px-3 py-1.5 rounded-lg flex items-center gap-2 transition-all duration-200 hover:scale-105 whitespace-nowrap"
                                    style={{ color: UI_COLORS.STATUS.INFO, backgroundColor: colorHelpers.withOpacity(UI_COLORS.STATUS.INFO, 0.08) }}
                                    title={t('common.actions.editUnloading')}
                                >
                                    <FontAwesomeIcon icon={faEdit} className="text-xs" />
                                    <span className="text-xs font-medium">Edit Unloading</span>
                                </button>
                                {!isSupervisor && (
                                    <button
                                        onClick={() => onGenerateChallan?.(a)}
                                        className="px-3 py-1.5 rounded-lg flex items-center gap-2 transition-all duration-200 hover:scale-105 whitespace-nowrap"
                                        style={{ color: UI_COLORS.STATUS.WARNING, backgroundColor: colorHelpers.withOpacity(UI_COLORS.STATUS.WARNING, 0.08) }}
                                        title={t('common.actions.generateChallan')}
                                    >
                                        <FontAwesomeIcon icon={faCheckDouble} className="text-xs" />
                                        <span className="text-xs font-medium">Generate Challan</span>
                                    </button>
                                )}
                            </>
                        )}

                        {/* "Mark as Complete" removed: its backend route
                            (POST /purchase-advices/{id}/mark-received) no longer exists,
                            so the button could only ever fail. */}

                        {status === 'received' && (
                            <span className="text-xs font-medium italic" style={{ color: UI_COLORS.STATUS.SUCCESS }}>
                                {t('purchaseAdvice.table.tooltips.adviceReceived')}
                            </span>
                        )}
                    </div>
                );
            },
            skeleton: <div className="w-32 h-8 bg-gray-200 rounded-lg mx-auto anim-pulse" />,
        },
        {
            id: 'actions',
            label: t('purchaseAdvice.table.columns.actions'),
            align: 'center',
            sortable: false,
            renderCell: (a) => {
                return (
                    <div className="flex items-center justify-center space-x-2">
                        <button
                            onClick={() => onViewAdvice?.(a.id)}
                            disabled={viewLoading}
                            className="p-2 rounded-lg transition-all duration-200 hover:scale-105 disabled:opacity-50"
                            style={{ color: UI_COLORS.TEXT.PRIMARY, backgroundColor: colorHelpers.withOpacity(UI_COLORS.NAVIGATION.HOVER, 0.35) }}
                            title={t('purchaseAdvice.table.tooltips.view')}
                        >
                            <FontAwesomeIcon icon={faEye} className="text-sm" />
                        </button>

                        {AUDIT_LOG_VIEWER_ROLES.includes(userRole) && (
                            <button
                                onClick={() => setAuditLogTarget({ id: a.id, label: a.advice_number })}
                                className="p-2 rounded-lg transition-all duration-200 hover:scale-105"
                                style={{ color: UI_COLORS.STATUS.INFO, backgroundColor: colorHelpers.withOpacity(UI_COLORS.STATUS.INFO, 0.1) }}
                                title={t('common.actions.auditLogs')}
                            >
                                <FontAwesomeIcon icon={faHistory} className="text-sm" />
                            </button>
                        )}

                        <button
                            onClick={() => handlePrintAdvice(a.id)}
                            disabled={printingAdviceId === a.id}
                            className="p-2 rounded-lg transition-all duration-200 hover:scale-105"
                            style={{ color: UI_COLORS.TEXT.PRIMARY, backgroundColor: colorHelpers.withOpacity(UI_COLORS.NAVIGATION.HOVER, 0.12) }}
                            title={t('purchaseAdvice.table.tooltips.print')}
                        >
                            {printingAdviceId === a.id ? (
                                <FontAwesomeIcon icon={faSpinner} spin className="text-sm" />
                            ) : (
                                <FontAwesomeIcon icon={faPrint} className="text-sm" />
                            )}
                        </button>

                        {['loading', 'advice_created'].includes((a.status || '').toLowerCase()) && (
                            <button
                                onClick={() => onEditAdvice?.(a.id)}
                                className="p-2 rounded-lg transition-all duration-200 hover:scale-105"
                                style={{ color: UI_COLORS.STATUS.WARNING, backgroundColor: colorHelpers.withOpacity(UI_COLORS.STATUS.WARNING, 0.1) }}
                                title={t('purchaseAdvice.table.tooltips.edit')}
                            >
                                <FontAwesomeIcon icon={faEdit} className="text-sm" />
                            </button>
                        )}
                    </div>
                );
            },
        },
    ], [t, handlePrintAdvice, onEditAdvice, printingAdviceId, onViewAdvice, viewLoading, onAssignSupervisor, onCompleteUnloading, onStartUnloading, onGenerateChallan, onEditUnloading, isSalesStaff, isSupervisor, userRole, setAuditLogTarget]);

    return (
        <>
            <DataTable
                columns={columns}
                data={advices}
                loading={loading}
                enablePagination={false}
                defaultSort={{ field: 'date', direction: 'desc' }}
                itemsPerPage={ITEMS_PER_PAGE}
                emptyState={{
                    title: t('purchaseAdvice.table.empty.title'),
                    message: t('purchaseAdvice.table.empty.message'),
                }}
            />

            <AuditLogsModal
                isOpen={!!auditLogTarget}
                onClose={() => setAuditLogTarget(null)}
                entityType="purchase_advice"
                entityId={auditLogTarget?.id}
                entityLabel={auditLogTarget?.label}
                hasTimeline={false}
            />
        </>
    );
};

export default PurchaseAdviceTable;
